import React, { useRef, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore'; 
import { db } from '../firebase';

const Signal = ({ userRank, onComboEnd }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  const engineRef = useRef({
    combo: 0,
    pendingXP: 0,
    starCharges: 0,
    globalMult: 1,
    isPausing: false,
    isLocked: false,
    spawnTimer: 0,
    packets: []
  });

  const [isDead, setIsDead] = useState(false);
  const [uiState, setUiState] = useState({ combo: 0, pendingXP: 0, starCharges: 0, globalMult: 1 });
  const [lastPayout, setLastPayout] = useState(null);
  const [leaderboardType, setLeaderboardType] = useState('daily');
  const [rankings, setRankings] = useState([]);

  const SCAN_Y_PERCENT = 0.75; 
  const BASE_XP = 2; 
  const THEME_COLOR = "#50e3c2";
  const STAR_COLOR = "#b2ffef";

  const syncUi = () => {
    const e = engineRef.current;
    setUiState({
      combo: e.combo,
      pendingXP: e.pendingXP,
      starCharges: e.starCharges,
      globalMult: e.globalMult
    });
  };

  useEffect(() => {
    const docRef = doc(db, "Global_Vars", "Main");
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        engineRef.current.globalMult = docSnap.data().globalXPMultiplier || 1;
        syncUi();
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isDead) return;
    const todayStr = new Date().toISOString().split('T')[0];
    const boardId = leaderboardType === 'daily' ? todayStr : 'all_time';
    const boardRef = doc(db, "Leaderboards_Signal", boardId);

    const unsubscribe = onSnapshot(boardRef, (docSnap) => {
      if (docSnap.exists()) setRankings(docSnap.data().rankings || []);
    });
    return () => unsubscribe();
  }, [isDead, leaderboardType]);

  const triggerPayoutDisplay = (amount) => {
    if (amount <= 0) return;
    setLastPayout(amount);
    setTimeout(() => setLastPayout(null), 3000);
  };

  const resetGame = () => {
    const e = engineRef.current;
    e.combo = 0;
    e.pendingXP = 0;
    e.starCharges = 0;
    e.packets = [];
    e.isPausing = false;
    e.isLocked = false;
    e.spawnTimer = 0;
    
    setIsDead(false);
    syncUi();
  };

  const handleIntercept = (e) => {
    if (e) e.stopPropagation();
    const engine = engineRef.current;
    if (engine.isLocked || isDead || engine.isPausing) return;

    let hitIndex = -1;
    for (let i = 0; i < engine.packets.length; i++) {
      const p = engine.packets[i];
      if ((p.isTarget || p.isStar) && Math.abs(p.y - SCAN_Y_PERCENT) < 0.07) {
        hitIndex = i;
        break;
      }
    }

    if (hitIndex !== -1) {
      const hit = engine.packets[hitIndex];
      if (hit.isStar) {
        engine.starCharges = 3;
      } else {
        const starBonus = engine.starCharges > 0 ? 2 : 1;
        const earned = Math.floor((BASE_XP * engine.globalMult * starBonus) * (engine.combo + 1));
        
        if (engine.starCharges > 0) engine.starCharges -= 1;
        engine.combo += 1;
        engine.pendingXP += earned;
      }

      engine.packets.splice(hitIndex, 1);
      syncUi();
    } else {
      const finalXP = engine.pendingXP;
      const finalCombo = engine.combo;

      if (finalXP > 0) {
        triggerPayoutDisplay(finalXP);
        onComboEnd(finalXP, finalCombo);
      }
      engine.isPausing = true;
      syncUi();

      setTimeout(() => {
        setIsDead(true);
        engine.combo = 0;
        engine.pendingXP = 0;
        engine.starCharges = 0;
        engine.packets = [];
        syncUi();
      }, 1500);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return; 
    const ctx = canvas.getContext('2d', { alpha: false }); 
    let frame;
    let lastTime = null;
    let smoothedDt = 1.0; 

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        canvas.width = Math.floor(entry.contentRect.width);
        canvas.height = Math.floor(entry.contentRect.height);
      }
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    const draw = (currentTime) => {
      if (isDead) return;
      
      if (!canvas.width || !canvas.height) {
        frame = requestAnimationFrame(draw);
        return;
      }

      if (lastTime === null) {
        lastTime = currentTime;
        frame = requestAnimationFrame(draw);
        return;
      }

      const elapsed = currentTime - lastTime;
      lastTime = currentTime;
      const cappedElapsed = Math.min(elapsed, 100);
      const targetTickRate = 1000 / 60;
      const rawDt = cappedElapsed / targetTickRate;

      smoothedDt = smoothedDt + 0.2 * (rawDt - smoothedDt);

      ctx.fillStyle = '#080d0d';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const engine = engineRef.current;

      ctx.strokeStyle = engine.isLocked ? 'rgba(255, 68, 68, 0.4)' : (engine.starCharges > 0 ? STAR_COLOR : `${THEME_COLOR}4D`);
      ctx.setLineDash([10, 5]);
      ctx.lineWidth = engine.starCharges > 0 ? 2 : 1;
      ctx.beginPath();
      ctx.moveTo(0, canvas.height * SCAN_Y_PERCENT);
      ctx.lineTo(canvas.width, canvas.height * SCAN_Y_PERCENT);
      ctx.stroke();

      if (!engine.isPausing) {
        engine.spawnTimer += cappedElapsed;
        if (engine.spawnTimer > 500) engine.spawnTimer = 500;

        while (engine.spawnTimer >= targetTickRate) {
          engine.spawnTimer -= targetTickRate;
          const spawnChance = 0.03 + (engine.combo * 0.002);
          if (Math.random() < Math.min(spawnChance, 0.1)) {
            const isStarSpawn = Math.random() < 0.01;
            const isTargetSpawn = isStarSpawn ? false : Math.random() < 0.25;

            let rawLabel = "⬢";
            if (!isStarSpawn) {
              const str = Math.random().toString(36).slice(2, 6).toUpperCase();
              rawLabel = isTargetSpawn ? `[${str}]` : str;
            }

            engine.packets.push({ 
              x: 0.1 + Math.random() * 0.8, 
              y: -0.1, 
              isTarget: isTargetSpawn,
              isStar: isStarSpawn,
              label: rawLabel
            });
          }
        }
      }

      const baseSpeed = 0.0015 + (userRank * 0.0002) + (engine.combo * 0.00015);

      for (let i = engine.packets.length - 1; i >= 0; i--) {
        const p = engine.packets[i];

        if (!engine.isPausing) {
          const frameSpeed = p.isStar ? baseSpeed * 2.5 : baseSpeed;
          p.y += frameSpeed * smoothedDt;
        }

        const xPos = p.x * canvas.width;
        const yPos = p.y * canvas.height;

        if (p.isStar) {
          ctx.font = 'bold 16px "JetBrains Mono"';
          ctx.fillStyle = STAR_COLOR; 
          ctx.shadowBlur = 10; 
          ctx.shadowColor = STAR_COLOR;
        } else if (p.isTarget) {
          ctx.font = '10px "JetBrains Mono", monospace';
          ctx.fillStyle = THEME_COLOR; 
          ctx.shadowBlur = 5; 
          ctx.shadowColor = THEME_COLOR;
        } else {
          ctx.font = '10px "JetBrains Mono", monospace';
          ctx.shadowBlur = 0; 
          ctx.fillStyle = `${THEME_COLOR}33`;
        }

        ctx.fillText(p.label, xPos, yPos);

        if (!engine.isPausing && p.y > SCAN_Y_PERCENT + 0.1) {
          if (p.isTarget) {
            const finalXP = engine.pendingXP;
            const finalCombo = engine.combo;

            if (finalXP > 0) {
              triggerPayoutDisplay(finalXP);
              onComboEnd(finalXP, finalCombo);
            }
            engine.isPausing = true;
            setTimeout(() => setIsDead(true), 1500);
            return; 
          } else {
            engine.packets.splice(i, 1);
          }
        }
      }

      ctx.shadowBlur = 0;
      frame = requestAnimationFrame(draw);
    };

    frame = requestAnimationFrame(draw);

    return () => { 
      cancelAnimationFrame(frame); 
      resizeObserver.disconnect(); 
    };
  }, [userRank, isDead, onComboEnd]);

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#080d0d' }}>
      <div style={{ position: 'absolute', top: '8px', left: '8px', fontFamily: 'monospace', pointerEvents: 'none', zIndex: 10, opacity: (isDead || engineRef.current.isPausing) ? 0.2 : 1 }}>
        <div style={{ fontSize: '18px', color: uiState.starCharges > 0 ? STAR_COLOR : THEME_COLOR, fontWeight: 'bold' }}>{uiState.combo}x</div>
        {uiState.globalMult > 1 && (
          <div style={{ fontSize: '9px', color: '#ffaa00', fontWeight: 'bold' }}>GLOBAL {uiState.globalMult}x</div>
        )}
        {uiState.starCharges > 0 && <div style={{ color: STAR_COLOR, fontSize: '9px', fontWeight: 'bold' }}>PACKET BURST: {uiState.starCharges}</div>}
        <div style={{ fontSize: '9px', color: '#fff', opacity: 0.6 }}>
            VAL: {Math.floor(BASE_XP * uiState.globalMult * (uiState.starCharges > 0 ? 2 : 1) * (uiState.combo + 1))}
        </div>
        <div style={{ fontSize: '9px', color: THEME_COLOR }}>+{uiState.pendingXP} EXP</div>
      </div>

      {lastPayout && (
        <div style={{ position: 'absolute', top: '40%', fontFamily: 'monospace', color: THEME_COLOR, fontSize: '10px', fontWeight: 'bold', animation: 'fadeOut 3s forwards', zIndex: 20 }}>
          + {lastPayout} EXP ACQUIRED
        </div>
      )}

      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', filter: isDead ? 'brightness(0.25)' : 'none', transition: 'filter 0.4s ease' }} />
      
      {isDead && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '140px', fontFamily: 'monospace' }}>
                <div style={{ color: '#50E3C2', fontSize: '8px', textAlign: 'center', marginBottom: '8px', opacity: 0.8 }}>LEADERBOARDS</div>
                <div style={{ borderBottom: '1px solid rgba(80, 227, 194, 0.15)', marginBottom: '6px', paddingBottom: '2px' }}>
                    <div style={{ color: THEME_COLOR, fontSize: '8px', fontWeight: 'bold', textAlign: 'center' }}>{leaderboardType === 'daily' ? 'DAILY' : 'ALL-TIME'}</div>
                </div>
                <div style={{ minHeight: '90px' }}>
                    {rankings.slice(0, 10).map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8px', color: i === 0 ? STAR_COLOR : 'rgba(255,255,255,1.0)', marginBottom: '2px' }}>
                            <span style={{ opacity: 0.4 }}>{i + 1}</span>
                            <span className="display-name-truncate">{r.displayName}</span>
                            <span style={{ color: THEME_COLOR }}>{r.xp}</span>
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                    <button onClick={() => setLeaderboardType(prev => prev === 'daily' ? 'all_time' : 'daily')} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '7px', cursor: 'pointer' }}>[ VIEW {leaderboardType === 'daily' ? 'ALL-TIME' : 'DAILY'} ]</button>
                    <button onClick={resetGame} style={{ background: 'transparent', border: `1px solid ${THEME_COLOR}`, color: THEME_COLOR, fontSize: '8px', padding: '4px 12px', cursor: 'pointer' }}>RECONNECT</button>
                </div>
            </div>
        </div>
      )}

      {!isDead && (
        <div style={{ position: 'absolute', bottom: '10px', zIndex: 10 }}>
            <button
              onClick={handleIntercept}
              disabled={engineRef.current.isLocked || engineRef.current.isPausing}
              style={{
                padding: '4px 15px',
                background: (engineRef.current.isLocked || engineRef.current.isPausing) ? 'rgba(255,68,68,0.1)' : `${uiState.starCharges > 0 ? STAR_COLOR : THEME_COLOR}1A`,
                border: `1px solid ${(engineRef.current.isLocked || engineRef.current.isPausing) ? '#ff4444' : (uiState.starCharges > 0 ? STAR_COLOR : THEME_COLOR)}`,
                color: (engineRef.current.isLocked || engineRef.current.isPausing) ? '#ff4444' : (uiState.starCharges > 0 ? STAR_COLOR : THEME_COLOR),
                fontFamily: 'monospace',
                fontSize: '9px',
                cursor: (engineRef.current.isLocked || engineRef.current.isPausing) ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                boxShadow: (engineRef.current.isLocked || engineRef.current.isPausing) ? 'none' : `0 0 8px ${uiState.starCharges > 0 ? STAR_COLOR : THEME_COLOR}33`
              }}
            >
              {(engineRef.current.isLocked || engineRef.current.isPausing) ? "REBOOTING" : "INTERCEPT"}
            </button>
        </div>
      )}

      <style>{`@keyframes fadeOut { 0% { opacity: 0; transform: translateY(5px); } 10% { opacity: 1; transform: translateY(0); } 80% { opacity: 1; } 100% { opacity: 0; transform: translateY(-15px); } }`}</style>
    </div>
  );
};

export default Signal;